import * as React from "react";
import { AuswahlAnker } from "./AuswahlAnker";
import "./notizbloecke.css";

/**
 * Die Leiste über einer Markierung im Notizfeld (PO 18.09.2026: „und wenn ich
 * textmarkiere, dann kann ich das auch als erledigt markieren").
 *
 * ══ SIE NIMMT KEINEN FOKUS ═════════════════════════════════════════════════
 * `onMouseDown` mit `preventDefault` an beiden Knöpfen: Ohne das nähme der
 * Klick dem `contentEditable` den Fokus, der Browser räumte die Markierung
 * weg — und die Leiste wirkte auf nichts mehr. Derselbe Griff wie an den
 * Auswahllisten hinter `@` und `#` und an der Formatierungsleiste.
 *
 * Der PLATZ kommt von `AuswahlAnker`: ein Portal am `body` mit
 * `position: fixed`, das sich an das Rechteck der Markierung hängt. Innerhalb
 * der Seite läge die Leiste sonst in einer Karte mit `overflow: hidden` und
 * wäre am oberen Rand abgeschnitten.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const Sprechblase: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M17 11.2a2.3 2.3 0 0 1-2.3 2.3H7.6L4 16.5v-3h-.7a2.3 2.3 0 0 1-2.3-2.3V5.8A2.3 2.3 0 0 1 3.3 3.5h11.4A2.3 2.3 0 0 1 17 5.8z" />
    </svg>
);

const Haken: React.FC = () => (
    <svg
        width={14}
        height={14}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M4.5 10.5 8 14l7.5-8" />
    </svg>
);

export const MarkierLeiste: React.FC<{
    feld: HTMLElement | null;
    onKommentar: () => void;
    onErledigt: () => void;
}> = ({ feld, onKommentar, onErledigt }) => {
    /*
     * AUF TOUCH SCHON BEIM BERÜHREN (Plan „Vorgangsdetail mobil", PR 6 —
     * Entscheidung 6). Mit dem Finger räumt das Antippen die Auswahl weg, und
     * das System schließt sein Auswahlmenü — bis der `click` ankommt, kann
     * die Leiste schon fort sein. Deshalb löst ein Finger/Stift beim
     * `pointerdown` aus; der folgende `click` wird geschluckt. Mit der Maus
     * bleibt alles beim Alten.
     */
    const schonRef = React.useRef(false);
    const knopf = (aktion: () => void) => ({
        onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
        onPointerDown: (e: React.PointerEvent) => {
            if (e.pointerType === "mouse") return;
            e.preventDefault();
            schonRef.current = true;
            aktion();
        },
        onClick: () => {
            if (schonRef.current) {
                schonRef.current = false;
                return;
            }
            aktion();
        },
    });
    return (
        <AuswahlAnker feld={feld}>
            <div className="nb-markleiste" role="group" aria-label="Markierte Stelle">
                <button type="button" className="nb-markleiste-btn" {...knopf(onKommentar)}>
                    <Sprechblase />
                    Kommentar
                </button>
                <button type="button" className="nb-markleiste-btn" {...knopf(onErledigt)}>
                    <Haken />
                    Erledigt
                </button>
            </div>
        </AuswahlAnker>
    );
};
